import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

// ============================================
// SCHEMAS
// ============================================

export const missionStepSchema = z.object({
  id: z.string(),
  text: z.string(),
  isOptional: z.boolean().optional().default(false),
});

export const missionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  sourceNodeId: z.string().nullable().optional(),
  title: z.string(),
  description: z.string().nullable().optional(),
  category: z.string().default("practice"),
  difficulty: z.string().default("easy"),
  estimatedTime: z.string().nullable().optional(),
  steps: z.array(missionStepSchema).nullable().optional(),
  why: z.string().nullable().optional(),
  successCriteria: z.string().nullable().optional(),
  xpReward: z.number().default(0),
  badgeId: z.string().nullable().optional(),
  status: z.enum(["PENDING", "IN_PROGRESS", "COMPLETED", "SKIPPED"]),
  stepsCompleted: z.array(z.string()).nullable().optional(),
  addedAt: z.string(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  reminderEnabled: z.boolean().default(false),
  nextReminderAt: z.string().nullable().optional(),
});

export const missionStatsSchema = z.object({
  pending: z.number(),
  inProgress: z.number(),
  completed: z.number(),
  total: z.number(),
});

// ============================================
// TYPES
// ============================================

export type Mission = z.infer<typeof missionSchema>;
export type MissionStep = z.infer<typeof missionStepSchema>;
export type MissionStats = z.infer<typeof missionStatsSchema>;
export type MissionStatus = Mission["status"];

export interface CreateMissionInput {
  sourceNodeId?: string;
  title: string;
  description?: string;
  category?: string;
  difficulty?: string;
  estimatedTime?: string;
  steps?: MissionStep[];
  why?: string;
  successCriteria?: string;
  xpReward?: number;
  badgeId?: string;
  reminderEnabled?: boolean;
}

export interface UpdateMissionInput {
  status?: MissionStatus;
  stepsCompleted?: string[];
  reminderEnabled?: boolean;
  nextReminderAt?: string;
}

// ============================================
// HOOKS
// ============================================

/**
 * Get all missions for the current user
 */
export const useMissions = (status?: MissionStatus) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["missions", status],
    queryFn: async () => {
      const token = await getToken();
      const params = status ? `?status=${status}` : "";
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/app/missions${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return missionSchema.array().parse(response.data);
    },
  });
};

/**
 * Get mission statistics
 */
export const useMissionStats = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["missions", "stats"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/app/missions/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return missionStatsSchema.parse(response.data);
    },
  });
};

/**
 * Get a single mission by ID
 */
export const useMission = (missionId: string) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["missions", missionId],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/app/missions/${missionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return missionSchema.parse(response.data);
    },
    enabled: !!missionId,
  });
};

/**
 * Create a new mission
 */
export const useCreateMission = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateMissionInput) => {
      const token = await getToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/missions`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return missionSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["missions"] });
    },
  });
};

/**
 * Update a mission
 */
export const useUpdateMission = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      missionId,
      ...input
    }: UpdateMissionInput & { missionId: string }) => {
      const token = await getToken();
      const response = await axios.patch(
        `${import.meta.env.VITE_API_URL}/app/missions/${missionId}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return missionSchema.parse(response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["missions"] });
      queryClient.invalidateQueries({ queryKey: ["missions", variables.missionId] });
    },
  });
};

/**
 * Delete a mission
 */
export const useDeleteMission = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (missionId: string) => {
      const token = await getToken();
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/app/missions/${missionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["missions"] });
    },
  });
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const getCategoryIcon = (category: string): string => {
  const icons: Record<string, string> = {
    setup: "⚙️",
    practice: "🎯",
    share: "👥",
    verify: "✓",
  };
  return icons[category] ?? "🎯";
};

export const getDifficultyColor = (difficulty: string): string => {
  const colors: Record<string, string> = {
    easy: "text-green-600 bg-green-100",
    medium: "text-amber-600 bg-amber-100",
    challenging: "text-red-600 bg-red-100",
  };
  return colors[difficulty] ?? colors.easy;
};

export const getStatusColor = (status: MissionStatus): string => {
  const colors: Record<MissionStatus, string> = {
    PENDING: "text-slate-600 bg-slate-100",
    IN_PROGRESS: "text-blue-600 bg-blue-100",
    COMPLETED: "text-green-600 bg-green-100",
    SKIPPED: "text-gray-400 bg-gray-100",
  };
  return colors[status];
};

export const getStatusLabel = (status: MissionStatus): string => {
  const labels: Record<MissionStatus, string> = {
    PENDING: "To Do",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Done",
    SKIPPED: "Skipped",
  };
  return labels[status];
};
